"use client";

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { Box } from '@/components/atoms/Box';
import { Text } from '@/components/atoms/Text';
import { ChevronDown } from 'lucide-react';
import { NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger } from '@/components/atoms/navigation-menu';
import type { NavMenuItemProps, DropdownGroup } from './NavMenuItem.props';

/**
 * Renders a grouped dropdown section with a label header and nested links.
 */
function NavDropdownGroup(t0) {
  const $ = _c(9);
  const {
    group
  } = t0;
  let t1;
  if ($[0] !== group.label) {
    t1 = <Box className="px-3 py-2 text-base font-bold uppercase tracking-wider text-muted-foreground">{group.label}</Box>;
    $[0] = group.label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== group.items) {
    t2 = group.items.map(_temp);
    $[2] = group.items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <ul>{t2}</ul>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <li>{t1}{t3}</li>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

/**
 * Renders a flat list of dropdown link items (no group headers).
 */
function _temp(item) {
  return <li key={item.title}><NavigationMenuLink asChild={true}><Link href={item.href} aria-label={item.title} className={cn("group flex gap-3 rounded-md p-3 leading-none no-underline outline-hidden transition-all select-none", "hover:bg-accent hover:text-accent-foreground", "focus:bg-accent focus:text-accent-foreground", "focus-visible:ring-2 focus-visible:ring-ring/50")}><Box className="transition-transform duration-200 group-hover:translate-x-0.5"><Box className="mb-1 text-base leading-none font-medium">{item.title}</Box>{item.description && <Text as="p" className="line-clamp-2 text-base leading-snug text-muted-foreground mt-1.5">{item.description}</Text>}</Box></Link></NavigationMenuLink></li>;
}
function NavDropdownFlatItems(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp2);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <>{t1}</>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

/**
 * NavMenuItem molecule.
 *
 * Renders a single navigation menu item — either a flat link or a
 * dropdown with optional grouped sections. Built on shadcn NavigationMenu.
 */
function _temp2(item) {
  return <li key={item.title}><NavigationMenuLink asChild={true}><Link href={item.href} aria-label={item.title} className={cn("group flex gap-3 rounded-md p-3 leading-none no-underline outline-hidden transition-all select-none", "hover:bg-accent hover:text-accent-foreground", "focus:bg-accent focus:text-accent-foreground", "focus-visible:ring-2 focus-visible:ring-ring/50")}><Box className="transition-transform duration-200 group-hover:translate-x-0.5"><Box className="mb-1 text-base leading-none font-medium">{item.title}</Box>{item.description && <Text as="p" className="line-clamp-2 text-base leading-snug text-muted-foreground mt-1.5">{item.description}</Text>}</Box></Link></NavigationMenuLink></li>;
}
export function NavMenuItem(t0) {
  const $ = _c(30);
  const {
    link,
    isTransparent
  } = t0;
  const hasDropdown = link.dropdownItems && link.dropdownItems.length > 0 || link.dropdownGroups && link.dropdownGroups.length > 0;
  if (!hasDropdown) {
    const t1 = link.href;
    const t2 = link.label;
    const t3 = !isTransparent ? "text-foreground hover:text-primary" : "text-white hover:text-white/80";
    let t4;
    if ($[0] !== t3) {
      t4 = cn("group/link relative block select-none space-y-1 rounded-md px-3 py-2 leading-none no-underline outline-none uppercase text-base font-medium", "transition-colors duration-200 ease-in-out", t3);
      $[0] = t3;
      $[1] = t4;
    } else {
      t4 = $[1];
    }
    let t5;
    if ($[2] !== link.label) {
      t5 = <Text className="relative z-10">{link.label}</Text>;
      $[2] = link.label;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    let t6;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Text className="absolute bottom-0 left-0 h-0.5 w-full origin-left bg-primary scale-x-0 transition-transform duration-300 ease-in-out group-hover/link:scale-x-100" />;
      $[4] = t6;
    } else {
      t6 = $[4];
    }
    let t7;
    if ($[5] !== link.href || $[6] !== link.label || $[7] !== t4 || $[8] !== t5) {
      t7 = <NavigationMenuItem><Link href={t1} aria-label={t2} className={t4}>{t5}{t6}</Link></NavigationMenuItem>;
      $[5] = link.href;
      $[6] = link.label;
      $[7] = t4;
      $[8] = t5;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  }
  const t1 = isTransparent ? "!text-white hover:!text-white/80 focus:!text-white data-[state=open]:!text-white" : "!text-foreground hover:!text-primary focus:!text-primary data-[state=open]:!text-foreground";
  let t2;
  if ($[10] !== t1) {
    t2 = cn("!bg-transparent px-1.5 uppercase cursor-pointer !text-base", "data-[state=open]:!bg-transparent", "hover:!bg-transparent focus:!bg-transparent", "transition-colors duration-200 ease-in-out", t1);
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = link.href;
  const t4 = link.label;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("inline-flex h-8 w-max items-center justify-center rounded-md px-2 py-1 text-base font-medium transition-colors", "focus-visible:ring-ring/50 outline-none focus-visible:ring-[var(--aq-ring-3)] focus-visible:outline-1");
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ChevronDown className="relative top-px ml-1 size-3 transition duration-300 group-data-[state=open]:rotate-180" aria-hidden="true" />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== link.href || $[15] !== link.label) {
    t7 = <Link href={t3} aria-label={t4} className={t5}>{link.label}{t6}</Link>;
    $[14] = link.href;
    $[15] = link.label;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t2 || $[18] !== t7) {
    t8 = <NavigationMenuTrigger asChild={true} className={t2}>{t7}</NavigationMenuTrigger>;
    $[17] = t2;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t10;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("shadow-lg shadow-black/10", "bg-popover text-popover-foreground", "border border-border/50", "rounded-lg");
    t10 = cn("w-[var(--aq-width-nav-min)] md:w-[var(--aq-width-nav-max)] p-3", "max-h-[var(--aq-nav-panel-height)] overflow-y-auto", "scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent");
    $[20] = t10;
    $[21] = t9;
  } else {
    t10 = $[20];
    t9 = $[21];
  }
  let t11;
  if ($[22] !== link.dropdownGroups || $[23] !== link.dropdownItems) {
    t11 = link.dropdownGroups ? link.dropdownGroups.map(_temp3) : link.dropdownItems && <NavDropdownFlatItems items={link.dropdownItems} />;
    $[22] = link.dropdownGroups;
    $[23] = link.dropdownItems;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11) {
    t12 = <NavigationMenuContent className={t9}><ul className={t10}>{t11}</ul></NavigationMenuContent>;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t8) {
    t13 = <NavigationMenuItem>{t8}{t12}</NavigationMenuItem>;
    $[27] = t12;
    $[28] = t8;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}
function _temp3(group, groupIndex) {
  return <React.Fragment key={group.label}>{groupIndex > 0 && <li><Box className="my-2 border-t border-border/50" /></li>}<NavDropdownGroup group={group} /></React.Fragment>;
}